import React, { useMemo } from 'react';
import {
  UserGroupIcon,
  CubeIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShieldExclamationIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  FolderIcon,
  RectangleStackIcon,
  BriefcaseIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import { useProducts } from '../hooks/useProducts';
import { useWarehouses } from '../hooks/useWarehouses';
import { useInventory } from '../hooks/useInventory';
import { useCategories } from '../hooks/useCategories';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import CommonPageHeader from '../components/common/CommonPageHeader';

const Dashboard = () => {
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { warehouses } = useWarehouses();
  const { inventory } = useInventory();
  const { categories } = useCategories();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { user } = useAuth();


  // Aktuális időszak meghatározása a megfelelő üdvözléshez
  const currentHour = new Date().getHours();
  let greeting = t('dashboard.welcome.afternoon');
  
  if (currentHour < 12) {
    greeting = t('dashboard.welcome.morning');
  } else if (currentHour < 18) {
    greeting = t('dashboard.welcome.afternoon');
  } else {
    greeting = t('dashboard.welcome.evening');
  }

  // Quick navigation tiles - csak ügyfelek és flottakezelés
  const allQuickNavTiles = [
    {
      name: t('nav.customers'),
      href: '/customers',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      description: 'Ügyfélkezelés',
      count: customers.length,
      module: 'customers'
    },
    {
      name: t('nav.vehicles'),
      href: '/fleet',
      icon: TruckIcon,
      color: 'from-indigo-500 to-indigo-600',
      description: 'Járművek',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.bookings'),
      href: '/fleet/bookings',
      icon: ClipboardDocumentListIcon,
      color: 'from-purple-500 to-purple-600',
      description: 'Foglalások',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.customerVehicles'),
      href: '/fleet/customers',
      icon: UserGroupIcon,
      color: 'from-teal-500 to-teal-600',
      description: 'Ügyfél járművek',
      count: null,
      module: 'fleetManagement'
    },
    {
      name: t('nav.settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-gray-600',
      description: 'Beállítások',
      count: null,
      module: 'settings'
    }
  ];

  // Szűrés jogosultságok alapján
  const quickNavTiles = allQuickNavTiles.filter(tile => {
    return hasAccess(tile.module as keyof import('../types/user').UserModulePermissions);
  });

  // Készlet elemzések
  const inventoryStats = useMemo(() => {
    const totalItems = inventory.length;
    const lowStockItems = inventory.filter(item => 
      item.minQuantity && item.quantity < item.minQuantity
    ).length;
    const outOfStockItems = inventory.filter(item => item.quantity === 0).length;
    const totalValue = inventory.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + (item.quantity * (product?.purchasePrice || 0));
    }, 0);

    return {
      totalItems,
      lowStockItems,
      outOfStockItems,
      totalValue
    };
  }, [inventory, products]);

  // Legutóbb hozzáadott ügyfelek
  const recentCustomers = useMemo(() => {
    return [...customers]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [customers]);

  // Legutóbb hozzáadott termékek
  const recentProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.getTime() - a.createdAt.getTime();
        }
        return 0;
      })
      .slice(0, 5);
  }, [products]);

  // Alacsony készletű termékek
  const lowStockProducts = useMemo(() => {
    return inventory
      .filter(item => item.minQuantity && item.quantity < item.minQuantity)
      .sort((a, b) => {
        const aRatio = a.quantity / (a.minQuantity || 1);
        const bRatio = b.quantity / (b.minQuantity || 1);
        return aRatio - bRatio;
      })
      .slice(0, 5);
  }, [inventory]);

  // Ha nincs szervezet rendelve (kivéve super_admin), speciális üzenet
  if (!user?.organizationId && user?.role !== 'super_admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 text-blue-500 mb-8">
            <UserGroupIcon className="h-full w-full" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Üdvözöljük, {user?.displayName}!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            A fiókja beállítás alatt van. Kérjük, vegye fel a kapcsolatot a rendszergazdával a hozzáférés aktiválásához.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-center space-x-3 mb-4">
              <UserGroupIcon className="h-8 w-8 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900">Következő lépések</h3>
            </div>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Várja meg a rendszergazda jóváhagyását</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Ellenőrizze email fiókját meghívó levelek után</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Frissítse az oldalt időnként</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0 animate-fade-in">
      <CommonPageHeader
        title={t('dashboard.title')}
        subtitle={`${greeting}, ${user?.displayName || t('dashboard.welcome.user')}!`}
        icon={ChartBarIcon}
      />

      {/* Quick Navigation Tiles - Modern with Purple Header */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Purple gradient header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-4">
          <h2 className="text-lg font-semibold text-white flex items-center">
            ✨ Gyors navigáció
          </h2>
        </div>
        
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-transparent to-blue-500/20"></div>
        </div>
        
        <div className="relative p-6">
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {quickNavTiles.map((tile, index) => (
              <Link
                key={tile.name}
                to={tile.href}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-700 dark:to-gray-800/50 rounded-lg p-3 border border-gray-200/60 dark:border-gray-600/60 hover:shadow-lg hover:scale-105 hover:from-white hover:to-purple-50/30 dark:hover:to-purple-900/20 transition-all duration-200 ease-out"
              >
                {/* Simplified overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} opacity-0 group-hover:opacity-10 transition-opacity duration-200`}></div>
                
                <div className="relative z-10 flex flex-col items-center text-center space-y-2">
                  {/* Compact icon */}
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${tile.color} text-white shadow-md group-hover:shadow-lg transition-shadow duration-200`}>
                    <tile.icon className="w-5 h-5" />
                  </div>
                  
                  {/* Clean title only */}
                  <div className="space-y-1">
                    <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm leading-tight">
                      {tile.name}
                    </h3>
                    {tile.count !== null && (
                      <span className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                        {tile.count}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          </div>
      </div>

      {/* Készlet áttekintés - Modern with Header */}
      {inventoryStats.totalItems > 0 && (
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Gradient header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4">
            <h2 className="text-lg font-semibold text-white flex items-center">
              📊 {t('dashboard.inventoryOverview')}
            </h2>
          </div>
          
          {/* Subtle background */}
          <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 via-transparent to-pink-500/20"></div>
          </div>
          
          <div className="relative p-6">
            
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg hover:shadow-md hover:from-purple-50 hover:to-purple-100 dark:hover:from-purple-900/20 dark:hover:to-purple-800/20 transition-all duration-200">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{inventoryStats.totalItems}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.totalItems')}</div>
            </div>

            <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg hover:shadow-md hover:from-orange-100 hover:to-amber-200 dark:hover:from-orange-800/30 dark:hover:to-amber-800/30 transition-all duration-200">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{inventoryStats.lowStockItems}</div>
              <div className="text-sm text-orange-600 dark:text-orange-400">{t('dashboard.lowStock')}</div>
            </div>

            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-900/20 dark:to-rose-900/20 rounded-lg hover:shadow-md hover:from-red-100 hover:to-rose-200 dark:hover:from-red-800/30 dark:hover:to-rose-800/30 transition-all duration-200">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{inventoryStats.outOfStockItems}</div>
              <div className="text-sm text-red-600 dark:text-red-400">{t('dashboard.outOfStock')}</div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg hover:shadow-md hover:from-green-100 hover:to-emerald-200 dark:hover:from-green-800/30 dark:hover:to-emerald-800/30 transition-all duration-200">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {new Intl.NumberFormat('hu-HU', {
                  style: 'currency',
                  currency: 'HUF',
                  maximumFractionDigits: 0
                }).format(inventoryStats.totalValue)}
              </div>
              <div className="text-sm text-green-600 dark:text-green-400">{t('dashboard.totalValue')}</div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Részletes információk - 2025 Modern Design */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Legutóbbi ügyfelek */}
        <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/30 dark:border-gray-700/30 overflow-hidden group hover:shadow-3xl transition-all duration-500">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-900/10 dark:to-indigo-900/10 group-hover:from-blue-100/40 group-hover:to-indigo-100/40 dark:group-hover:from-blue-800/20 dark:group-hover:to-indigo-800/20 transition-all duration-500"></div>
          <div className="relative z-10 flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent flex items-center">
              👥 {t('dashboard.recentCustomers')}
            </h2>
            <Link 
              to="/customers" 
              className="px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg"
            >
              {t('dashboard.viewAll')} →
            </Link>
          </div>
          <div className="relative z-10 space-y-3">
            {recentCustomers.length > 0 ? (
              recentCustomers.map((customer, index) => (
                <div 
                  key={customer.id} 
                  className="group flex items-center justify-between p-4 bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm rounded-xl border border-white/40 dark:border-gray-600/40 hover:shadow-lg hover:scale-102 transition-all duration-300 hover:bg-white/80 dark:hover:bg-gray-700/80"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                      {customer.type === 'organization' 
                        ? customer.companyName 
                        : `${customer.lastName} ${customer.firstName}`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-300">
                      📧 {customer.email}
                    </p>
                  </div>
                  {customer.createdAt && (
                    <div className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-300">
                      {format(customer.createdAt, 'MM/dd')}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                <p className="text-sm">{t('dashboard.noCustomers')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Legutóbbi termékek */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Green gradient header */}
          <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center">
                🛍️ {t('dashboard.recentProducts')}
              </h2>
              <Link 
                to="/products" 
                className="px-3 py-1.5 text-sm font-medium bg-white/20 text-white rounded-full hover:bg-white/30 transition-all duration-200 hover:scale-105"
              >
                {t('dashboard.viewAll')} →
              </Link>
            </div>
          </div>
          
          {/* Subtle background */}
          <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 via-transparent to-teal-500/20"></div>
          </div>
          
          <div className="relative p-6">
          <div className="space-y-3">
            {recentProducts.length > 0 ? (
              recentProducts.map((product, index) => (
                <div 
                  key={product.id} 
                  className="group flex items-center justify-between p-4 bg-gradient-to-r from-white/60 to-green-50/30 dark:from-gray-700/60 dark:to-green-900/20 backdrop-blur-sm rounded-xl border border-white/40 dark:border-gray-600/40 hover:shadow-lg hover:scale-102 transition-all duration-200 hover:from-white/80 hover:to-green-100/50 dark:hover:to-green-800/30"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200">{product.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200">📦 {product.code}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200">
                      {new Intl.NumberFormat('hu-HU', {
                        style: 'currency',
                        currency: 'HUF',
                        maximumFractionDigits: 0
                      }).format(product.netPrice || 0)}
                    </div>
                    {product.createdAt && (
                      <div className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full group-hover:bg-green-100 dark:group-hover:bg-green-900/30 group-hover:text-green-600 dark:group-hover:text-green-400 transition-all duration-200">
                        {format(product.createdAt, 'MM/dd')}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                <p className="text-sm">{t('dashboard.noProducts')}</p>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Alacsony készletű termékek */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Orange gradient header */}
          <div className="bg-gradient-to-r from-orange-600 via-red-600 to-rose-600 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center">
                ⚠️ {t('dashboard.lowStockProducts')}
              </h2>
              <Link 
                to="/inventory" 
                className="px-3 py-1.5 text-sm font-medium bg-white/20 text-white rounded-full hover:bg-white/30 transition-all duration-200 hover:scale-105"
              >
                {t('dashboard.viewAll')} →
              </Link>
            </div>
          </div>
          
          {/* Subtle background */}
          <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-rose-500/20"></div>
          </div>
          
          <div className="relative p-6">
          <div className="space-y-3">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((item, index) => (
                <div 
                  key={item.id} 
                  className="group flex items-center justify-between p-4 bg-gradient-to-r from-red-50/80 to-orange-50/60 dark:from-red-900/30 dark:to-orange-900/20 backdrop-blur-sm rounded-xl border border-red-200/60 dark:border-red-700/40 hover:shadow-lg hover:scale-102 transition-all duration-200 hover:from-red-100/90 hover:to-orange-100/70 dark:hover:from-red-800/40 dark:hover:to-orange-800/30"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200">{item.productName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200">🏪 {item.warehouseName}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center mb-1">
                      <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-2 group-hover:text-red-600 transition-colors duration-200" />
                      <span className="text-sm font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded-full group-hover:bg-red-200 dark:group-hover:bg-red-800/50 transition-all duration-200">
                        {item.quantity} / {item.minQuantity}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center">{item.unit}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                <CheckCircleIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm">{t('dashboard.noLowStock')}</p>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 